import DimensionUnit = App.Enums.DimensionUnits;
import DimensionUnitTypes = App.Enums.DimensionUnitTypes;

type UnitDetail = {
	scale: number;
	modulo: number;
	paddingSize: number;
};

// ============================================================================

const unitCategories: Record<DimensionUnitTypes, DimensionUnit[]> = {
	default: ["default"],
	time: ["second", "minute", "hour", "day"],
	time_of_day: ["minute_of_day", "hour_of_day"],
	distance: ["kilometer"],
	money: ["eurocent", "euro"],
	percentage: ["percentage"],
};

// Scales are in seconds for the time category, allowing second/minute/hour/day to chain correctly.
export const unitValues: Record<DimensionUnit, UnitDetail> = {
	default: { scale: 1, modulo: Infinity, paddingSize: 1 },
	second: { scale: 1, modulo: 60, paddingSize: 2 },
	minute: { scale: 60, modulo: 60, paddingSize: 2 },
	hour: { scale: 3600, modulo: 24, paddingSize: 2 },
	day: { scale: 86400, modulo: Infinity, paddingSize: 1 },
	minute_of_day: { scale: 1, modulo: 60, paddingSize: 2 },
	hour_of_day: { scale: 60, modulo: Infinity, paddingSize: 2 },
	kilometer: { scale: 1, modulo: Infinity, paddingSize: 1 },
	eurocent: { scale: 1, modulo: 100, paddingSize: 2 },
	euro: { scale: 100, modulo: Infinity, paddingSize: 1 },
	percentage: { scale: 1, modulo: Infinity, paddingSize: 1 },
};

export function getUnitCategory(
	unit: DimensionUnit
): DimensionUnitTypes | null {
	for (const [category, units] of Object.entries(unitCategories)) {
		if (units.includes(unit)) {
			return category as DimensionUnitTypes;
		}
	}
	return null;
}

export function getAvailableUnits(
	selectedUnit: DimensionUnit
): DimensionUnit[] | null {
	const unitCategory = getUnitCategory(selectedUnit);

	if (unitCategory === null) return null;

	return unitCategories[unitCategory]
		.filter((unit) => unitValues[unit].scale >= unitValues[selectedUnit].scale)
		.reverse();
}

export function customTrunc(n: number, precision: number = 1e-4): number {
	if (Math.abs(n - Math.round(n)) < precision) {
		return Math.round(n);
	}
	return Math.trunc(n);
}

function valueToLabelSeconds(value: number): string {
	const sign = value < 0;
	const abs = Math.abs(customTrunc(value));
	const days = Math.floor(abs / 86400);
	const hours = Math.floor((abs % 86400) / 3600);
	const minutes = Math.floor((abs % 3600) / 60);
	const seconds = abs % 60;
	const hh = hours.toString().padStart(2, "0");
	const mm = minutes.toString().padStart(2, "0");
	const ss = seconds.toString().padStart(2, "0");
	const result =
		days > 0
			? `${days}d:${hh}:${mm}:${ss}`
			: hours > 0
			? `${hh}:${mm}:${ss}`
			: `${mm}:${ss}`;
	return (sign ? "-" : "") + result;
}

export function valueToLabel(
	value: number | null,
	selectedUnit: DimensionUnit,
	t: (key: string) => string
): string {
	if (value === null) return "";

	const availableUnits = getAvailableUnits(selectedUnit);
	if (!availableUnits) return "";

	let sign = false;
	const result = availableUnits.reduce((acc, unit) => {
		let v =
			customTrunc(
				(value * unitValues[selectedUnit].scale) / unitValues[unit].scale
			) % unitValues[unit].modulo;
		sign ||= v < 0;
		v = Math.abs(v);

		if (getUnitCategory(unit) === "money") {
			if (unit === "eurocent" && !v) {
				return acc;
			}
			return (
				acc +
				t(`units.${unit}.short`) +
				v.toString().padStart(unitValues[unit].paddingSize, "0")
			);
		}
		if (
			v === 0 &&
			(unitValues[unit].scale !== unitValues[selectedUnit].scale || acc)
		) {
			return acc;
		}
		return acc + v.toString() + t(`units.${unit}.short`);
	}, "");
	return (sign ? "-" : "") + result;
}
